import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SetPopupReducerData } from "../store/reducer/PopupReducer";
import {
  deleteEmployee,
  EmployeeeStatusChange,
  EmployeeListApi,
} from "../api/api";
import { useOrganizationId } from "../context/Customfunction";
import CustomTable from "../ui/custom-table";
import { toast } from "react-toastify";

function Employee() {
  const dispatch = useDispatch();
  const [employee, setEmployee] = useState([]);
  const orgId = useOrganizationId();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { showModal } = useSelector(
    (state) => state?.PopupReducer?.modal || {}
  );
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { user_id, organization_id } = useSelector(
    (state) => state?.authUser?.data?.data
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // Debounce by 500ms

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const fetchEmployee = useCallback(async () => {
    setLoading(true);

    try {
      const page = pagination.pageIndex + 1;
      const limit = pagination.pageSize;
      const search = debouncedSearch;
      const data = await EmployeeListApi(
        page,
        limit,
        search,
        user_id,
        organization_id
      );
      if (data?.data?.status === true) {
        setEmployee(data?.data?.data || []);

        setTotalRows(data?.data?.total_records || 0);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  }, [orgId, pagination.pageIndex, pagination.pageSize, debouncedSearch]);

  useEffect(() => {
    fetchEmployee();
  }, [pagination, debouncedSearch]);

  useEffect(() => {
    if (showModal === false) {
      fetchEmployee();
    }
  }, [showModal, debouncedSearch]);

  const handleStausChange = async (id, status) => {
    let payload = {
      employee_id: id,
      is_blocked: status === true ? false : true,
    };
    try {
      const data = await EmployeeeStatusChange(
        payload,
        user_id,
        organization_id
      );
      if (data?.data?.status === true) {
        fetchEmployee();
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "employee_name", header: "Employee Name" },
      {
        accessorKey: "employee_image",
        header: "Employee Image",
        Cell: ({ row }) => {
          const imagePath = row.original.employee_image;
          const imageUrl = imagePath
            ? `https://visionemptrack.greymatterz.com/${imagePath}`
            : "";

          return (
            imageUrl && (
              <img
                src={imageUrl}
                alt="Employee"
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            )
          );
        },
      },
      {
        accessorKey: "Status",
        header: "status",
        Cell: ({ row }) => {
          const isActive = row.original.is_blocked;

          return (
            <div className="d-flex align-items-center gap-2">
              <label className="custom-toggle mb-0">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={() => handleStausChange(row.original._id, isActive)}
                />
                <span className="slider"></span>
              </label>
              <span
                className={`fw-bold ${
                  isActive ? "text-success" : "text-danger"
                }`}
              >
                {isActive ? "UnBlock" : "Block"}
              </span>
            </div>
          );
        },
      },
      { accessorKey: "created_at", header: "Created At" },
    ],
    []
  );

  const handleAdd = () => {
    dispatch(
      SetPopupReducerData({
        modalType: "ADDEMPLOYE",
        showModal: true,
      })
    );
  };

  const handleEdit = (row) => {
    dispatch(
      SetPopupReducerData({
        modalType: "ADDEMPLOYE",
        showModal: true,
        data: row._id,
      })
    );
  };

  const handelDelete = async (row) => {
    const data = await deleteEmployee(row._id, user_id, organization_id);
    if (data) {
      toast("Record deleted successfully");
      fetchEmployee();
    }
  };

  return (
    <aside className="mainSection">
      <CustomTable
        columns={columns}
        data={employee}
        loading={loading}
        onAddClick={handleAdd}
        addButtonLabel="Add Employee"
        showActions={true}
        onEdit={handleEdit}
        onDelete={handelDelete}
        pagination={pagination}
        onPaginationChange={(updater) => {
          setPagination((prev) => {
            const newPagination =
              typeof updater === "function"
                ? updater(prev)
                : { ...prev, ...updater };
            return newPagination;
          });
        }}
        rowCount={totalRows}
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPagination({ pageIndex: 0, pageSize: 10 }); // Reset pagination on search
        }}
      />
    </aside>
  );
}

export default Employee;
